import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

export enum EventType {
  ReplyToEvents = "reply_to_events",
  SendOnSchedule = "send_on_schedule",
  Inquiry = "inquiry_question",
  Keyword = "keyword",
  Underwriting = "underwriting",
}

export enum RateEventType {
  Checkout = "checkout",
}

export enum TaskEventType {
  Checkin = "checkin_checkout",
}

export enum EditMode {
  Create = 1,
  Update = 2,
  Copy = 4,
}

export type TaskType = "cleaning" | "maintenance" | "reception" | "room_service";

@Injectable({
  providedIn: "root",
})
export class RobotRulesService {
  private readonly allChannelsApi = "api/chat/had_thirdparty";
  private readonly allHousesApi = "api/reservation/house_group_list";
  private readonly channelAccountsApi = "api/reservation/thirdparty_list";
  private readonly saveMessageRulesApi = "api/automation/message/rule_save";
  private readonly saveRateRulesApi = "api/automation/comment/rule_save";
  private readonly saveRateReplyRulesApi = "api/automation/comment/reply_guest_comment_rule_save";
  private readonly saveTaskRulesApi = "api/automation/task/rule_save";
  private readonly messageRuleDetailApi = "api/automation/message/rule_detail";
  private readonly rateRuleDetailApi = "api/automation/comment/rule_detail";
  private readonly rateReplyRuleDetailApi = "api/automation/comment/reply_guest_comment_rule_detail";
  private readonly taskRuleDetailApi = "api/automation/task/rule_detail";
  private readonly quesionListApi = "api/automation/message/intent_list";
  private readonly messageDynContentApi = "api/automation/message/dynamic_content_keys";
  private readonly rateDynContentApi = "api/automation/comment/dynamic_content_keys";
  private readonly testRuleApi = "api/automation/message/text_testing";
  private readonly cleanerListApi = "api/schedule/staff/list";

  readonly ListOfBookOption = [
    { label: "新的预定请求", value: "new_reservation_request", i18nKey: "robot.rule.book_event.new_res_request" },
    { label: "新的订单", value: "new_reservation_paid", i18nKey: "robot.rule.book_event.new_order" },
    { label: "订单被取消", value: "new_reservation_canceled", i18nKey: "robot.rule.book_event.res_canceled" },
  ];

  readonly ListOfCheckOption = [
    { label: "订单生成后", value: "after_new_reservation", i18nKey: "robot-robot_question_type_ts-订单生成后" },
    { label: "入住日前", value: "before_checkin", i18nKey: "automation-rule_events_msg_入住日前" },
    { label: "入住日", value: "on_checkin_day", i18nKey: "automation-rule_events_msg_入住日" },
    { label: "入住日后", value: "after_checkin", i18nKey: "automation-rule_events_msg_入住日后" },
    { label: "退房日前", value: "before_checkout", i18nKey: "automation-rule_events_msg_退房日前" },
    { label: "退房日", value: "on_checkout_day", i18nKey: "automation-rule_events_msg_退房日" },
    { label: "退房日后", value: "after_checkout", i18nKey: "automation-rule_events_msg_退房日后" },
    { label: "指定日期", value: "specific_day_of_week", i18nKey: "automation-rule_events_msg_指定日" },
  ];

  readonly ListOfAIOption = [{ label: "AI智能识别", value: "inquiry", i18nKey: "automation.list.option.ai" }];

  readonly ListOfKeywordOption = [{ label: "关键词识别", value: "keyword", i18nKey: "automation.list.option.keyword" }];

  readonly ListOfFallbackOption = [
    { label: "兜底回复", value: "underwriting", i18nKey: "automation.list.option.underwriting" },
  ];

  readonly ListOfCommentOption = [
    { i18nKey: "automation.list.option.rate_guest", value: "comment", label: "rate_guest" },
    { i18nKey: "automation.list.option.reply_guest", value: "guest_committed_comment", label: "reply_guest" },
  ];

  readonly ListOfTaskCheckinOption = [
    { label: "入住前", value: "before_checkin", i18nKey: "robot.rule.check_event.before_checkin" },
    { label: "入住后", value: "after_checkin", i18nKey: "robot.rule.check_event.after_checkin" },
  ];

  readonly ListOfTaskCheckoutOption = [
    { label: "退房前", value: "before_checkout", i18nKey: "robot.rule.check_event.before_checkout" },
    { label: "退房后", value: "after_checkout", i18nKey: "robot.rule.check_event.after_checkout" },
  ];

  readonly ListOfTaskTimeOption = [
    { label: "", i18nKey: "automation-task_time_opt_before_ci", value: "before_checkin" },
    { label: "", i18nKey: "automation-task_time_opt_after_co", value: "after_checkout" },
    { label: "", i18nKey: "automation-task_time_opt_on_day_ci", value: "on_checkin" },
    { label: "", i18nKey: "automation-task_time_opt_on_day_co", value: "on_checkout" },
  ];

  readonly ListOfTaskTypeOption = [
    { label: "", i18nKey: "automation-task_html_opt_cleaning", value: "cleaning" },
    { label: "", i18nKey: "automation-task_html_opt_maintenance", value: "maintenance" },
    { label: "", i18nKey: "automation-task_html_opt_reception", value: "reception" },
    { label: "", i18nKey: "automation-task_html_opt_room_service", value: "room_service" },
  ];

  constructor(private http: HttpClient) {}

  fetchAllChannels(params) {
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(this.allChannelsApi, { params: query }) as Observable<any>;
  }

  fetchAllHouses() {
    const query = new HttpParams({
      fromObject: { format: "group_by_delete" },
    });
    return this.http.get(this.allHousesApi, { params: query }) as Observable<any>;
  }

  fetchAllQuestion() {
    return this.http.get(this.quesionListApi) as Observable<any>;
  }

  fetchMessageDynContent() {
    return this.http.get(this.messageDynContentApi) as Observable<any>;
  }

  fetchRateDynContent() {
    return this.http.get(this.rateDynContentApi) as Observable<any>;
  }

  fetchTaskDynContent() {
    return this.http.get("/api/automation/task/dynamic_content_keys") as Observable<any>;
  }

  fetchChannelAccounts(params) {
    const query = new HttpParams({
      fromObject: params,
    });

    return this.http.get(this.channelAccountsApi, { params: query }) as Observable<any>;
  }

  updateMessageRobotRules(params) {
    return this.http.post(this.saveMessageRulesApi, params) as Observable<any>;
  }

  updateRateRobotRules(params) {
    return this.http.post(this.saveRateRulesApi, params) as Observable<any>;
  }

  updateRateReplyRobotRules(params) {
    return this.http.post(this.saveRateReplyRulesApi, params) as Observable<any>;
  }

  updateTaskRobotRules(params) {
    return this.http.post(this.saveTaskRulesApi, params) as Observable<any>;
  }

  updatePriceRobotRules(params) {
    return this.http.post("api/automation/price/rule_save", params) as Observable<any>;
  }

  fetchMessageRuleDetail(params) {
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(this.messageRuleDetailApi, { params: query }) as Observable<any>;
  }

  fetchRateRuleDetail(params) {
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(this.rateRuleDetailApi, { params: query }) as Observable<any>;
  }

  fetchRateReplyRuleDetail(params) {
    return this.http.get(this.rateReplyRuleDetailApi, { params: params }) as Observable<any>;
  }

  fetchTaskRuleDetail(params) {
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(this.taskRuleDetailApi, { params: query }) as Observable<any>;
  }

  fetchPriceRuleDetail(params) {
    return this.http.get("/api/automation/price/rule_detail", { params }) as Observable<any>;
  }

  fetchGapRuleDetail(params) {
    return this.http.get("/api/automation/gap/rule_detail", { params });
  }

  requestUpdateGapRule(params) {
    return this.http.post("/api/automation/gap/rule_save", params);
  }

  requestTestRule(params) {
    return this.http.post(this.testRuleApi, params) as Observable<any>;
  }

  fetchCleanerList() {
    const query = new HttpParams({
      fromObject: {
        only_active: "1",
      },
    });
    return this.http.get(this.cleanerListApi, { params: query }) as Observable<any>;
  }

  requestApplyToResvsForComment(params) {
    return this.http.post("/api/automation/comment/regenerate_plan", params);
  }

  requestApplyToResvsForMessage(params) {
    return this.http.post("/api/automation/message/regenerate_plan", params);
  }
}
